import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, ScoreRing, SkeletonList, QueryError } from '../../../../components/ui';

export interface ScheduleWidgetProps {
  meetings: { data?: any; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

export function ScheduleWidget({ meetings }: ScheduleWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();

  return (
    <DataCard title="Today's Schedule" subtitle={`${meetings.data?.meetings.length ?? 0} meetings`}>
      {meetings.isLoading ? <SkeletonList rows={3} /> : meetings.isError ? <QueryError error={meetings.error} onRetry={() => meetings.refetch()} compact /> : (
        <div className="space-y-2">
          {meetings.data?.meetings.slice(0, 4).map((m: any) => {
            const start = new Date(m.startAt);
            const now = new Date();
            const minsUntil = Math.round((start.getTime() - now.getTime()) / 60_000);
            const startingSoon = minsUntil > 0 && minsUntil <= 30;
            return (
              <div key={m.eventId} className={clsx('rounded-lg border px-3 py-2.5', startingSoon ? 'border-primary/30 bg-primary/3' : 'border-border')}>
                <button type="button" className="w-full text-left flex items-center justify-between cursor-pointer" onClick={() => { navigate(m.matchedClientId ? `${basePath}/clients/${m.matchedClientId}` : `${basePath}/clients`); }}>
                  <span className="text-2xs font-medium text-ink">{m.summary}</span>
                  <ScoreRing score={m.prepScore} size={28} strokeWidth={2.5} colorScale="prep" />
                </button>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-2xs text-ink-dim">
                    {start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {' — '}
                    {new Date(m.endAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </span>
                  {startingSoon && (
                    <StatusBadge status="info" label={`In ${minsUntil}m`} size="xs" pulse />
                  )}
                  <span className="flex-1" />
                  {m.joinUrl && (
                    <a
                      href={m.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-0.5 text-2xs font-semibold text-white hover:bg-primary/90 transition-colors"
                    >
                      <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                      Join
                    </a>
                  )}
                  {m.calendarUrl && (
                    <a
                      href={m.calendarUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-2xs font-medium text-ink-dim hover:text-ink hover:border-border-strong transition-colors"
                    >
                      <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                      Event
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          {(!meetings.data || meetings.data.meetings.length === 0) && (
            <p className="py-4 text-center text-2xs text-ink-dim">No meetings today</p>
          )}
        </div>
      )}
    </DataCard>
  );
}
